import { AfterViewInit, Directive, ElementRef, inject } from '@angular/core';

import { COOKIE_SETTINGS_HREF, CookieConsentService } from '../../services/cookie-consent/cookie-consent.service';

/**
 * Turns every `<a href="#cookie-settings">` inside the host into the way back into the consent kit's
 * Cookie settings dialog (AT-2749).
 *
 * The page footer's links are the design system's `at-footer`, which only knows how to draw
 * `FooterLink`s (`routerLink` or `href`) and has no click output. The kit's own dialog tells people the
 * link is "beside our privacy policy", so the link has to be one of those `FooterLink`s, drawn by the
 * footer like its neighbours, separator and all; this directive, on the footer, answers the click.
 *
 * A plain click keeps the browser off the link: behind `<base href="/">` the bare fragment would leave the
 * page for `/#cookie-settings`. That is exactly where the browser's own new-tab gestures should go, so
 * Ctrl, Cmd and Shift clicks pass through (a middle click is `auxclick` and never reaches here), and
 * `CookieConsentService.consumeSettingsLink` opens the dialog in the new tab. A `FooterLink` has no way to
 * carry an attribute either, so `aria-haspopup` goes on from here.
 */
@Directive({
  selector: '[appCookieSettingsLinks]',
  standalone: true,
  host: { '(click)': 'onClick($event)' },
})
export class CookieSettingsLinksDirective implements AfterViewInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly consent = inject(CookieConsentService);

  ngAfterViewInit(): void {
    // A view hook, because the footer's own view draws the links, and it renders before this runs.
    for (const link of this.host.querySelectorAll(`a[href="${COOKIE_SETTINGS_HREF}"]`)) {
      link.setAttribute('aria-haspopup', 'dialog');
    }
  }

  protected onClick(event: MouseEvent): void {
    const link = event.target instanceof Element ? event.target.closest(`a[href="${COOKIE_SETTINGS_HREF}"]`) : null;
    if (!link || !this.host.contains(link) || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.consent.openSettings();
  }
}
